// Talks directly to Adobe DMAT's window._satellite, mirroring the NASS Clinic Dashboard
// pattern. Channel/levels are already supplied to cdc-dmat.js via the query-string in
// public/index.html, so we do NOT need siteCatalyst.setLevelX / updateVariables here.

const CDI_CHANNEL = 'DPH CDI';                 // must match index.html `ch=` param
const CDI_SITE_SECTION = 'DPH CDI';

// ---------- Init / queue (waits for _satellite, then drains queued events) ----------

function _check_initialized() {
    window.CDC = window.CDC || {};
    window.CDC.SPA_App = window.CDC.SPA_App || {};

    if (!window.CDC.SPA_App.initialized) {
        if (window.hasOwnProperty('_satellite')) {
            window.CDC.SPA_App.initialized = true;
        } else if (!window.CDC.SPA_App.checkInterval) {
            // Poll until DMAT/_satellite is ready, then process anything queued.
            window.CDC.SPA_App.checkInterval = setInterval(function () {
                if (window.hasOwnProperty('_satellite')) {
                    window.CDC.SPA_App.initialized = true;
                    clearInterval(window.CDC.SPA_App.checkInterval);
                    window.CDC.SPA_App.checkInterval = null;
                    _process_queue();
                }
            }, 100);
        }
    }
}

function _queue_action(action) {
    window.CDC.SPA_App.action_queue = window.CDC.SPA_App.action_queue || [];
    window.CDC.SPA_App.action_queue.push(action);
}

function _process_queue() {
    if (window.CDC.SPA_App.initialized && window.CDC.SPA_App.action_queue && window.CDC.SPA_App.action_queue.length > 0) {
        const actions = window.CDC.SPA_App.action_queue;
        window.CDC.SPA_App.action_queue = [];
        actions.forEach(function (action) {
            if (action.function === '_trigger_interaction') {
                _trigger_interaction(action.eventLabel, action.eventType, action.eventValue);
            } else if (action.function === '_trigger_pageview') {
                _trigger_pageview(action.dataObject);
            }
        });
    }
}

// ---------- Direct _satellite calls (mirror NASS Utility.js) ----------

function _trigger_interaction(eventLabel, eventType, eventValue) {
    if (window.hasOwnProperty('_satellite')) {
        const dataObject = {};
        eventType = eventType || 'o';
        dataObject.ch = CDI_CHANNEL;
        dataObject.pageName = document.title;
        dataObject.prop40 = eventValue;
        dataObject.label = eventLabel;
        dataObject.interactionType = eventType;
        dataObject.interactionValue = 'ci-' + eventLabel + ': ' + eventValue;
        dataObject.prop46 = window.location.href;

        if (process.env.REACT_APP_ENABLE_ANALYTIC_CONSOLE_LOG === 'true') {
            console.log('adobe dmat satellite track: interaction', dataObject);
        }
        window._satellite.track('interaction', dataObject);
    } else {
        console.error('Analytics: _satellite is undefined when firing interaction');
    }
}

function _trigger_pageview(dataObject) {
    if (window.hasOwnProperty('_satellite')) {
        const currentTitle = dataObject.pageName || document.title;

        // Mirror props on window.s so anything else that reads them sees the right values.
        if (window.s) {
            window.s.pageName = currentTitle;
            window.s.channel = dataObject.channel || CDI_CHANNEL;
            window.s.prop61 = currentTitle;
            window.s.prop69 = currentTitle;
            window.s.prop73 = window.location.href;
            window.s.eVar61 = currentTitle;
        }

        // Build the same enhanced payload NASS sends.
        const enhancedData = {
            ...dataObject,
            pageName: currentTitle,
            channel: dataObject.channel || CDI_CHANNEL,
            siteSection: dataObject.siteSection || CDI_SITE_SECTION,
            page: currentTitle,
            pid: currentTitle,
            v61: currentTitle,
            prop61: currentTitle,
            prop69: currentTitle,
            prop73: window.location.href,
            pageURL: '',
            pageURLOriginal: '',
            eVar61: currentTitle,
            force_pagename: currentTitle,
            override_url_pagename: true
        };

        if (process.env.REACT_APP_ENABLE_ANALYTIC_CONSOLE_LOG === 'true') {
            console.log('adobe dmat satellite track: pageview', enhancedData);
        }
        window._satellite.track('pageview', enhancedData);
    } else {
        console.error('Analytics: _satellite is undefined when firing pageview');
    }
}

// ---------- Public API (same names as before, so other files don't change) ----------

// De-dupe so route changes that fire twice in <2s don't double-count
let pageViewTimeout = null;
let lastFiredPageName = null;
let lastFiredTime = 0;

function do_interaction(interaction) {
    _check_initialized();
    _queue_action({
        function: '_trigger_interaction',
        eventLabel: 'CDI',
        eventType: 'o',
        eventValue: interaction
    });
    _process_queue();

    if (process.env.REACT_APP_ENABLE_ANALYTIC_CONSOLE_LOG === 'true') {
        console.log('interaction: ' + interaction);
    }
}

function do_triggerPageView(title) {
    if (!title) return;

    document.title = title;

    const now = Date.now();
    if (title === lastFiredPageName && (now - lastFiredTime) < 2000) {
        if (process.env.REACT_APP_ENABLE_ANALYTIC_CONSOLE_LOG === 'true') {
            console.log('Duplicate pageview suppressed:', title);
        }
        return;
    }
    lastFiredPageName = title;
    lastFiredTime = now;

    if (pageViewTimeout) clearTimeout(pageViewTimeout);

    // Small debounce lets React finish committing route state before we read window.location.
    pageViewTimeout = setTimeout(() => {
        _check_initialized();
        _queue_action({
            function: '_trigger_pageview',
            dataObject: {
                pageName: title,
                channel: CDI_CHANNEL,
                siteSection: CDI_SITE_SECTION,
                force_pagename: title,
                override_url_pagename: true
            }
        });
        _process_queue();
    }, 100);
}

// Kept for backward compatibility with App.js. DMAT already sets channel/levels via the
// query string in public/index.html, so there is nothing to configure here. We just make
// sure _satellite polling is started so the first pageview drains as soon as DMAT loads.
function configureAdobeLaunchMetrics() {
    try {
        _check_initialized();
        if (process.env.REACT_APP_ENABLE_ANALYTIC_CONSOLE_LOG === 'true') {
            console.log('activity: analytics config (DMAT)');
        }
    } catch (e) {
        if (process.env.REACT_APP_ENABLE_ANALYTIC_CONSOLE_LOG === 'true') {
            console.log('error configuring analytics', e);
        }
    }
}

export { do_interaction, do_triggerPageView, configureAdobeLaunchMetrics };